import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";

import "../styles.css";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";

const { Column } = Table;
const { Text } = Typography;

const MAX_LEVELS = 8;

/**
 * Watch Campaign (global daily level ladder) — single campaign config.
 * Levels reset daily at IST midnight; each level must finish in one
 * session; coins are paid once per completed level. Series order below is
 * the queue-building priority: users are served these series top-down,
 * minus what they have already watched.
 */
const WatchCampaignConfig = () => {
  const { token, apiUrl } = useContext(Context);

  const [campaign, setCampaign] = useState(null);
  const [series, setSeries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [counter, setCounter] = useState(0);
  const [addSeriesId, setAddSeriesId] = useState(null);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/watch-campaign-config/`, token, (result) => {
      if (result && result.campaign) setCampaign(result.campaign);
    });
  }, [apiUrl, token, counter]);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      setSeries(result.webseries || []);
    });
  }, [apiUrl, token]);

  const seriesById = useMemo(() => {
    const map = {};
    series.forEach((s) => {
      map[s._id] = s;
    });
    return map;
  }, [series]);

  const set = (patch) => setCampaign((c) => ({ ...c, ...patch }));
  const setNested = (key, patch) =>
    setCampaign((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));

  const save = (extra) => {
    const c = { ...campaign, ...(extra || {}) };
    const body = {
      name: c.name,
      status: c.status,
      subscriber_enabled: !!c.subscriber_enabled,
      audience: c.audience || "ag_registered",
      ag_paywall_free: c.ag_paywall_free !== false,
      continuation_window_minutes: c.continuation_window_minutes ?? 15,
      picker_autopick_seconds: c.picker_autopick_seconds ?? 15,
      ag_auto_resume: c.ag_auto_resume !== false,
      daily_coin_cap: c.daily_coin_cap || 0,
      pool_dry_behavior: c.pool_dry_behavior || "hide_levels",
      session: c.session,
      penalty: c.penalty,
      messaging: c.messaging,
      min_watch_pct: c.min_watch_pct || 80,
      exclude_shareable: c.exclude_shareable !== false,
      series_order: c.series_order || [],
      levels: c.levels || [],
      info_points: c.info_points || [],
      info_points_hi: c.info_points_hi || [],
    };
    setSaving(true);
    Put(
      `${apiUrl}/v4/watch-campaign-config/`,
      token,
      body,
      (result) => {
        setSaving(false);
        if (result && result.campaign) {
          setCampaign(result.campaign);
          notification.success({ message: "Watch Campaign saved" });
        } else {
          notification.error({
            message: (result && result.detail) || "Save failed",
          });
          setCounter((n) => n + 1);
        }
      },
      () => {
        setSaving(false);
        notification.error({ message: "Save failed" });
        setCounter((n) => n + 1);
      }
    );
  };

  if (!campaign) return null;

  const levels = campaign.levels || [];
  const order = campaign.series_order || [];

  const updateLevel = (index, patch) => {
    const next = levels.map((lvl, i) => (i === index ? { ...lvl, ...patch } : lvl));
    set({ levels: next });
  };

  const addLevel = () => {
    if (levels.length >= MAX_LEVELS) return;
    const prev = levels[levels.length - 1];
    set({
      levels: [
        ...levels,
        {
          level: levels.length + 1,
          required_videos: prev ? prev.required_videos + 2 : 3,
          coins_per_video: prev ? prev.coins_per_video + 10 : 10,
          min_watch_pct: prev ? prev.min_watch_pct : campaign.min_watch_pct || 80,
          session_minutes: prev && prev.session_minutes ? prev.session_minutes + 10 : null,
        },
      ],
    });
  };

  const removeLevel = (index) => {
    const next = levels
      .filter((_, i) => i !== index)
      .map((lvl, i) => ({ ...lvl, level: i + 1 }));
    set({ levels: next });
  };

  const moveSeries = (index, delta) => {
    const next = [...order];
    const j = index + delta;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    set({ series_order: next });
  };

  const active = campaign.status === "active";

  return (
    <div className="manage-tasks">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Alert
          type={active ? "success" : "warning"}
          showIcon
          message={
            active
              ? `Campaign LIVE (version ${campaign.version || 0}) — daily reset at IST midnight.`
              : "Campaign paused — users see nothing until activated."
          }
          description="One level per session; coins credit once per completed level. In-flight sessions keep the config they started with."
          action={
            <Switch
              checked={active}
              checkedChildren="Active"
              unCheckedChildren="Paused"
              loading={saving}
              onChange={(checked) =>
                save({ status: checked ? "active" : "paused" })
              }
            />
          }
        />

        <Card title="Levels (max 8 — sequential unlock)" size="small">
          <Table dataSource={levels.map((l, i) => ({ ...l, key: i }))} pagination={false} size="small">
            <Column title="Level" dataIndex="level" width={70} />
            <Column
              title="Required videos"
              render={(_, r, i) => (
                <InputNumber
                  min={1}
                  value={r.required_videos}
                  onChange={(v) => updateLevel(i, { required_videos: v || 1 })}
                />
              )}
            />
            <Column
              title="Coins / video"
              render={(_, r, i) => (
                <InputNumber
                  min={0}
                  value={r.coins_per_video}
                  onChange={(v) => updateLevel(i, { coins_per_video: v || 0 })}
                />
              )}
            />
            <Column
              title="Min watch %"
              render={(_, r, i) => (
                <InputNumber
                  min={1}
                  max={100}
                  value={r.min_watch_pct}
                  onChange={(v) => updateLevel(i, { min_watch_pct: v || 80 })}
                />
              )}
            />
            <Column
              title="Session (min)"
              render={(_, r, i) => (
                <InputNumber
                  min={1}
                  placeholder="global"
                  value={r.session_minutes}
                  onChange={(v) => updateLevel(i, { session_minutes: v || null })}
                />
              )}
            />
            <Column
              title="Level reward"
              render={(_, r) => (
                <Tag color="gold">{r.required_videos * r.coins_per_video} coins</Tag>
              )}
            />
            <Column
              width={60}
              render={(_, r, i) => (
                <Popconfirm title="Remove level?" onConfirm={() => removeLevel(i)}>
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            />
          </Table>
          <Button
            style={{ marginTop: 12 }}
            icon={<PlusOutlined />}
            disabled={levels.length >= MAX_LEVELS}
            onClick={addLevel}
          >
            Add level
          </Button>
        </Card>

        <Card
          title="Series priority order (queue built top-down, minus watched)"
          size="small"
        >
          <Table
            dataSource={order.map((id, i) => ({ key: id, id, index: i }))}
            pagination={false}
            size="small"
          >
            <Column title="#" render={(_, r) => r.index + 1} width={50} />
            <Column
              title="Series"
              render={(_, r) =>
                (seriesById[r.id] && seriesById[r.id].title) || (
                  <Text type="danger">{r.id} (not found)</Text>
                )
              }
            />
            <Column
              title="Episodes"
              width={100}
              render={(_, r) =>
                (seriesById[r.id] && seriesById[r.id].numberOfEpisodes) || "-"
              }
            />
            <Column
              width={140}
              render={(_, r) => (
                <Space>
                  <Button
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={r.index === 0}
                    onClick={() => moveSeries(r.index, -1)}
                  />
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={r.index === order.length - 1}
                    onClick={() => moveSeries(r.index, 1)}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      set({ series_order: order.filter((id) => id !== r.id) })
                    }
                  />
                </Space>
              )}
            />
          </Table>
          <Space style={{ marginTop: 12 }}>
            <Select
              showSearch
              allowClear
              placeholder="Add series"
              style={{ width: 320 }}
              value={addSeriesId}
              onChange={setAddSeriesId}
              optionFilterProp="children"
            >
              {series
                .filter((s) => !order.includes(s._id))
                .map((s) => (
                  <Select.Option key={s._id} value={s._id}>
                    {s.title}
                  </Select.Option>
                ))}
            </Select>
            <Button
              icon={<PlusOutlined />}
              disabled={!addSeriesId}
              onClick={() => {
                set({ series_order: [...order, addSeriesId] });
                setAddSeriesId(null);
              }}
            >
              Add
            </Button>
          </Space>
        </Card>

        <Card title="Rules" size="small">
          <Space size="large" wrap>
            <Space direction="vertical" size={2}>
              <Text>Session window (minutes)</Text>
              <InputNumber
                min={1}
                value={(campaign.session || {}).window_minutes}
                onChange={(v) => setNested("session", { window_minutes: v || 1 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Skip penalty</Text>
              <Space>
                <Switch
                  checked={(campaign.penalty || {}).enabled !== false}
                  onChange={(v) => setNested("penalty", { enabled: v })}
                />
                <InputNumber
                  min={0}
                  max={10}
                  addonBefore="max +"
                  value={(campaign.penalty || {}).max_per_level}
                  onChange={(v) => setNested("penalty", { max_per_level: v || 0 })}
                />
              </Space>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Daily coin cap (0 = uncapped here)</Text>
              <InputNumber
                min={0}
                value={campaign.daily_coin_cap}
                onChange={(v) => set({ daily_coin_cap: v || 0 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Pool dry behaviour</Text>
              <Select
                style={{ width: 220 }}
                value={campaign.pool_dry_behavior || "hide_levels"}
                onChange={(v) => set({ pool_dry_behavior: v })}
              >
                <Select.Option value="hide_levels">
                  Hide remaining levels
                </Select.Option>
                <Select.Option value="least_watched">
                  Serve least-watched (0 coins)
                </Select.Option>
              </Select>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Audience</Text>
              <Select
                style={{ width: 200 }}
                value={campaign.audience || "ag_registered"}
                onChange={(v) => set({ audience: v })}
              >
                <Select.Option value="ag_registered">
                  AG-registered users only
                </Select.Option>
                <Select.Option value="all">All MiniPIX users</Select.Option>
              </Select>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Subscribers can play</Text>
              <Switch
                checked={!!campaign.subscriber_enabled}
                onChange={(v) => set({ subscriber_enabled: v })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Picker autopick (sec, 0 = off)</Text>
              <InputNumber
                min={0}
                value={campaign.picker_autopick_seconds}
                onChange={(v) => set({ picker_autopick_seconds: v || 0 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Level chaining window (min)</Text>
              <InputNumber
                min={0}
                value={campaign.continuation_window_minutes}
                onChange={(v) => set({ continuation_window_minutes: v || 0 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>AG users: no paywall</Text>
              <Switch
                checked={campaign.ag_paywall_free !== false}
                onChange={(v) => set({ ag_paywall_free: v })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>AG users: auto-resume levels</Text>
              <Switch
                checked={campaign.ag_auto_resume !== false}
                onChange={(v) => set({ ag_auto_resume: v })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Exclude shareable content</Text>
              <Switch
                checked={campaign.exclude_shareable !== false}
                onChange={(v) => set({ exclude_shareable: v })}
              />
            </Space>
          </Space>
        </Card>

        <Card title="Reminders (abandoned / expired sessions)" size="small">
          <Space size="large" wrap>
            <Space direction="vertical" size={2}>
              <Text>Enabled</Text>
              <Switch
                checked={(campaign.messaging || {}).enabled !== false}
                onChange={(v) => setNested("messaging", { enabled: v })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Remind after (minutes)</Text>
              <InputNumber
                min={1}
                value={(campaign.messaging || {}).remind_after_minutes}
                onChange={(v) =>
                  setNested("messaging", { remind_after_minutes: v || 1 })
                }
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>SMS max / day</Text>
              <InputNumber
                min={0}
                value={(campaign.messaging || {}).sms_max_per_day}
                onChange={(v) => setNested("messaging", { sms_max_per_day: v || 0 })}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>SMS DLT template id (empty = no SMS)</Text>
              <Input
                style={{ width: 260 }}
                value={(campaign.messaging || {}).sms_template_id || ""}
                onChange={(e) =>
                  setNested("messaging", { sms_template_id: e.target.value || null })
                }
              />
            </Space>
          </Space>
        </Card>

        <Card title="Info points (app 'how it works')" size="small">
          <Text type="secondary">One point per line.</Text>
          <Divider style={{ margin: "8px 0" }} />
          <Space direction="vertical" style={{ width: "100%" }}>
            <Input.TextArea
              rows={3}
              placeholder="English"
              value={(campaign.info_points || []).join("\n")}
              onChange={(e) =>
                set({ info_points: e.target.value.split("\n") })
              }
            />
            <Input.TextArea
              rows={3}
              placeholder="Hindi"
              value={(campaign.info_points_hi || []).join("\n")}
              onChange={(e) =>
                set({ info_points_hi: e.target.value.split("\n") })
              }
            />
          </Space>
        </Card>

        <Button type="primary" loading={saving} onClick={() => save()}>
          Save Watch Campaign
        </Button>
      </Space>
    </div>
  );
};

export default WatchCampaignConfig;
